/* eslint-disable */

import React, { useEffect, useState, useMemo } from 'react'
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Container, ContentHeader, Title, ActionToolbar, ContentBody, SearchContainer } from './Eaters.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectShouldOpen,
  selectMessage,
  selectSeverity,
  setOpen,
  selectEater,
  selectPageSize,
  setPageSize
} from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { useNavigate, useParams } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { BreadCrumbLink } from '../../components/PageContainer/PageContainer';
import { DiningReview } from './types'
import styled from 'styled-components';
import { selectRestaurantWithMenuByAlias } from '../Restaurant/slice';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const DiningReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ReviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff8c42;
    box-shadow: 0 4px 12px rgba(10, 30, 61, 0.1);
    transform: translateY(-2px);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #0a1e3d;

  div {
    margin: 0;
  }

  strong {
    color: #0a1e3d;
    font-weight: 600;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;

  button {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #0a1e3d;
  white-space: nowrap;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  button {
    font-size: 12px;
    padding: 6px 12px;
  }

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const PageSizeControl = styled.div`
  min-width: 150px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DiningReviews: React.FC = () => {
  const openServer = useAppSelector(selectShouldOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {eaterid} = useParams();
  const person = useAppSelector(selectEater);
  const [reviews, setReviews] = useState(person?.reviews);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = useAppSelector(selectPageSize);

  const breadcrumbLinks: BreadCrumbLink[] = [
    {
      name: 'Eaters',
      url: '/eaters'
    },
    {
      name: person?.firstName + " " + person?.lastName??'',
      url:`/eaters/${person?.eaterId}`
    }
  ]

  useEffect(() => {
    setReviews(person?.reviews);
  }, [person?.reviews])

  const enrichedReviews = useMemo(() => {
    return reviews?.map(review => {
      const restaurant = useAppSelector(selectRestaurantWithMenuByAlias(review.restaurantAlias));
      const dishName = restaurant?.menus?.find(menu => menu.menuID === review.menuId)?.dishes.find(dish => dish.dishID === review.dishId)?.name;
      return {
        ...review,
        restaurantName: restaurant?.name,
        dishName: dishName
      }
    }) ?? [];
  }, [reviews])

  const filteredReviews = useMemo(() => {
    if (!searchQuery.trim()) {
      return enrichedReviews;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return enrichedReviews.filter(review =>
      (review.restaurantName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (review.dishName?.toLowerCase().includes(lowerQuery) ?? false) ||
      (review.sentimentLabel?.toLowerCase().includes(lowerQuery) ?? false)
    );
  }, [enrichedReviews, searchQuery]);

  const totalReviews = filteredReviews.length;
  const totalPages = Math.ceil(totalReviews / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalReviews);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;
  const paginatedReviews = filteredReviews.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery])

  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }

  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  return (
    <PageContainer title='Dining Reviews' breadcrumbs={breadcrumbLinks} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Container>
        <ContentHeader>
          <Title>Dining Reviews</Title>
          <ActionToolbar>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/eaters/${person?.eaterId}/dining-reviews/add-dining-review`)}
            >
              Add Review
            </Button>
          </ActionToolbar>
        </ContentHeader>

        <SearchContainer>
          <TextField
            placeholder="Search by restaurant, dish, or sentiment..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        <ContentBody>
          <DiningReviewsGrid>
            {paginatedReviews.map((review) => (
              <ReviewCard
                key={review.diningReviewId}
                onClick={() => navigate(`/eaters/${eaterid}/dining-reviews/${review.diningReviewId}`)}
              >
                <CardContent>
                  <CardInfo>
                    <div><strong>Restaurant:</strong> {review.restaurantName}</div>
                    <div><strong>Dish:</strong> {review.dishName}</div>
                    <div><strong>Sentiment:</strong> {review.sentimentLabel}</div>
                    <div><strong>Score:</strong> {review.sentimentScore}</div>
                    <div><strong>Summary:</strong> {review.sentimentSummary}</div>
                  </CardInfo>
                </CardContent>
                <CardActions>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/eaters/${eaterid}/dining-reviews/${review.diningReviewId}`);
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </ReviewCard>
            ))}
          </DiningReviewsGrid>
        </ContentBody>

        {totalReviews > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {totalReviews > 0 ? startIndex + 1 : 0} to {endIndex} of {totalReviews} reviews
            </PaginationInfo>

            <PaginationControls>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasPreviousPage}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <PaginationInfo>
                Page {currentPage + 1} of {totalPages}
              </PaginationInfo>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasNextPage}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </PaginationControls>

            <PageSizeControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={pageSize}
                  label="Page Size"
                  onChange={(e) => {
                    dispatch(setPageSize(Number(e.target.value)));
                    setCurrentPage(0);
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                </Select>
              </FormControl>
            </PageSizeControl>
          </PaginationContainer>
        )}
      </Container>
    </PageContainer>
  )
}

export default withAuthenticationRequired(DiningReviews)
